import React, { useEffect } from 'react';
import styled from 'styled-components';

const AdContainer = styled.div`
  margin: 2rem 0;
  
  .OUTBRAIN {
    margin: 1rem 0;
  }
  
  .desktop-ad {
    display: block;
  }
  
  .mobile-ad {
    display: none;
  }
  
  @media (max-width: 768px) {
    .desktop-ad {
      display: none !important;
    }
    .mobile-ad {
      display: block !important;
    }
  }
`;

interface OutbrainAdsProps {
  type?: 'footer' | 'longscroll';
}

const OutbrainAds: React.FC<OutbrainAdsProps> = ({ type = 'footer' }) => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  // Get unique_req from URL parameters
  const getUniqueReq = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('unique_req');
    }
    return null;
  };

  // Fire beacon function
  const fireBeacon = (uniqueReq: string) => {
    if (uniqueReq) {
      const beaconUrl = `https://ytentioniau.org/pixel?unique_req=${encodeURIComponent(uniqueReq)}`;
 
      if (navigator.sendBeacon) {
        navigator.sendBeacon(beaconUrl);
      } else {
        const img = new Image();
        img.src = beaconUrl;
      }
    }
  };

  useEffect(() => {
    // Load Outbrain script if not already loaded
    if (typeof window !== 'undefined' && !window.OBR) {
      const script = document.createElement('script');
      script.src = '//widgets.outbrain.com/outbrain.js';
      script.async = true;
      script.type = 'text/javascript';
      document.head.appendChild(script);
    }

    // Set up click tracking for Outbrain ads
    const uniqueReq = getUniqueReq();
    if (uniqueReq) {
      const handleOutbrainClick = (e: Event) => {
        // Check if the clicked element is within an Outbrain ad
        const target = e.target as Element;
        const outbrainElement = target.closest('.OUTBRAIN, [data-widget-id]');
        if (outbrainElement) {
          fireBeacon(uniqueReq);
        }
      };

      // Add click listener to document to catch dynamically loaded Outbrain ads
      document.addEventListener('click', handleOutbrainClick, true);

      // Also set up a MutationObserver to watch for Outbrain ads being loaded
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              const element = node as Element;
              // Check if this is an Outbrain ad or contains Outbrain ads
              const outbrainAds = element.matches && element.matches('.OUTBRAIN, [data-widget-id]') 
                ? [element] 
                : element.querySelectorAll && element.querySelectorAll('.OUTBRAIN, [data-widget-id]');
              
              if (outbrainAds && outbrainAds.length > 0) {
                outbrainAds.forEach((ad: Element) => {
                  ad.addEventListener('click', () => fireBeacon(uniqueReq), true);
                });
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Cleanup function
      return () => {
        document.removeEventListener('click', handleOutbrainClick, true);
        observer.disconnect();
      };
    }
  }, []);

  const getWidgetIds = () => {
    if (type === 'footer') {
      return {
        desktop: 'AR_1',
        mobile: 'CRMB_1'
      };
    } else if (type === 'longscroll') {
      return {
        desktop: 'AR_2',
        mobile: 'CRMB_2'
      };
    }
    return { desktop: 'AR_1', mobile: 'CRMB_1' };
  };

  const widgetIds = getWidgetIds();

  return (
    <AdContainer>
      {/* Desktop Ad */}
      <div 
        className="OUTBRAIN desktop-ad" 
        data-src={currentUrl} 
        data-widget-id={widgetIds.desktop}
      ></div>
      
      {/* Mobile Ad */}
      <div 
        className="OUTBRAIN mobile-ad" 
        data-src={currentUrl} 
        data-widget-id={widgetIds.mobile}
      ></div>
    </AdContainer>
  );
};

// Declare global OBR for TypeScript
declare global {
  interface Window {
    OBR: any;
  }
}

export default OutbrainAds;
